import React, {useEffect, useRef, useState} from "react"
import {loadPayments} from "@payjp/payments-js"
import axios from "axios"

type PropTypes = {
  api_key: string
  fetch_path: string
  secure_url: string
  email: string
  phone_number: string
  locale: string
}

const New: React.FC<PropTypes> = ({
  api_key,
  fetch_path,
  secure_url,
  email,
  phone_number,
  locale,
}) => {
  const [payments, setPayments] = useState<any>(null)
  const [widgets, setWidgets] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [message, setMessage] = useState<string>("")
  const [clientSecret, setClientSecret] = useState<string>("")

  const paymentFormRef = useRef<HTMLDivElement>(null)

  // 1. client_secret をバックエンドから取得
  const fetchClientSecret = async () => {
    try {
      setLoading(true)
      const response = await axios.get(fetch_path)

      setClientSecret(response.data?.client_secret)
      return response.data?.client_secret
    } catch (error) {
      console.error("client_secret取得エラー:", error)
      setMessage("決済準備中にエラーが発生しました")
      return null
    } finally {
      setLoading(false)
    }
  }

  // 2. PAY.JP Payments.js の初期化
  useEffect(() => {
    const initializePayments = async () => {
      try {
        const payInstance = await loadPayments(api_key, {
          locale: locale,
        })
        setPayments(payInstance)
      } catch (error) {
        console.log(api_key)
        console.error("Payments.js 初期化エラー:", error)
        setMessage("決済フォームの初期化に失敗しました")
      }
    }

    initializePayments()
  }, [])

  // 3. client_secret取得後 → フォームをマウント
  useEffect(() => {
    if (!payments || !clientSecret) return

    let mountedForm: any = null

    const mountPaymentForm = async () => {
      try {
        const widgetInstance = payments.widgets({
          clientSecret,
          locale: locale,
        })

        setWidgets(widgetInstance)

        mountedForm = widgetInstance.createForm("payment", {
          layout: "tab",
          paymentMethodOrder: ["card"],
          fields: {
            billingDetails: {
              name: "auto",
              email: "never",
              phone: "never",
            },
          },
        })

        mountedForm.mount(paymentFormRef.current)

        mountedForm.on("ready", () => {
          console.log("PAY.JP フォーム準備完了")
        })
      } catch (error) {
        console.error("フォームマウントエラー:", error)
        setMessage("決済フォームの表示に失敗しました")
      }
    }

    mountPaymentForm()

    // クリーンアップ
    return () => {
      if (mountedForm) {
        mountedForm.unmount()
      }
      if (paymentFormRef.current) {
        paymentFormRef.current.innerHTML = ""
      }
    }
  }, [payments, clientSecret])

  // 支払い確定ボタン
  const handleConfirmPayment = async () => {
    if (!widgets) {
      setMessage("フォームがまだ準備できていません")
      return
    }

    setLoading(true)
    setMessage("")

    try {
      const result = await widgets.confirmSetup({
        return_url: secure_url, // 実際の完了ページURLに変更

        // ★★★ 任意の請求先情報をここで送信 ★★★
        payment_method_data: {
          billing_details: {
            email: email, // ← 任意のメールアドレス
            phone: phone_number, // ← 任意の電話番号（+81 形式推奨）
            // address: {                         // 住所も必要なら追加
            //   line1: '東京都渋谷区神宮前1-1-1',
            //   city: '渋谷区',
            //   postal_code: '150-0001',
            //   country: 'JP',
            // },
          },
        },
      })

      if (result.error) {
        setMessage(`決済エラー: ${result.error.message}`)
      } else {
        setMessage("支払い情報の登録が正常に完了しました！")
      }
    } catch (error: any) {
      console.error(error)
      setMessage("このカードでは取引できません")
    } finally {
      setLoading(false)
    }
  }

  // 初回レンダリング時にclient_secretを取得
  useEffect(() => {
    fetchClientSecret()
  }, [])

  return (
    <div style={{maxWidth: "520px", margin: "40px auto", padding: "20px"}}>
      <div
        ref={paymentFormRef}
        style={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "24px",
          backgroundColor: "#ffffff",
          minHeight: "320px",
        }}
      />

      <button
        onClick={handleConfirmPayment}
        disabled={loading || !widgets || !clientSecret}
        style={{
          marginTop: "24px",
          width: "100%",
          padding: "14px",
          fontSize: "16px",
          backgroundColor: "#000000",
          color: "#ffffff",
          border: "none",
          borderRadius: "6px",
          cursor: loading || !widgets ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "処理中..." : "登録する"}
      </button>

      {message && (
        <p
          style={{
            marginTop: "16px",
            padding: "12px",
            borderRadius: "4px",
            backgroundColor: message.includes("エラー") ? "#fee" : "#efe",
            color: message.includes("エラー") ? "#c00" : "#080",
          }}
        >
          {message}
        </p>
      )}
    </div>
  )
}

export default New
